import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GraduationCap, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back!");
            navigate("/dashboard");
        } catch (err) {
            toast.error(apiError(err));
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-background">
            <div className="hidden lg:flex flex-col justify-between bg-secondary text-secondary-foreground p-12 grid-bg">
                <Link to="/" className="flex items-center gap-2"><span className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground"><GraduationCap className="h-5 w-5" /></span><span className="font-heading text-lg font-extrabold">LARUTA<span className="text-primary">.</span></span></Link>
                <div>
                    <h2 className="font-heading text-3xl font-bold leading-tight">Learn → Practice → Create → Demonstrate → Progress</h2>
                    <p className="mt-4 text-secondary-foreground/70">Practical Digital & AI Education for Every Generation.</p>
                </div>
                <p className="font-mono text-sm text-secondary-foreground/40">Experience &gt; Certificate</p>
            </div>
            <div className="flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-sm">
                    <Link to="/" className="lg:hidden flex items-center gap-2 mb-8"><span className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground"><GraduationCap className="h-5 w-5" /></span><span className="font-heading text-lg font-extrabold">LARUTA.</span></Link>
                    <h1 className="font-heading text-2xl font-bold">Log in to LARUTA</h1>
                    <p className="mt-1 text-sm text-foreground/60">Welcome back — continue your learning journey.</p>
                    <form onSubmit={submit} className="mt-8 space-y-4">
                        <div><Label htmlFor="email">Email</Label><Input id="email" type="email" data-testid="login-email-input" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" /></div>
                        <div><Label htmlFor="password">Password</Label><Input id="password" type="password" data-testid="login-password-input" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" /></div>
                        <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="login-submit-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}</Button>
                    </form>
                    <div className="my-5 flex items-center gap-3 text-xs text-foreground/40"><span className="flex-1 h-px bg-border" /> OR <span className="flex-1 h-px bg-border" /></div>
                    <Button variant="outline" onClick={googleLogin} className="w-full rounded-full" data-testid="google-login-btn"><Mail className="mr-2 h-4 w-4" /> Continue with Google</Button>
                    <p className="mt-6 text-center text-sm text-foreground/60">No account? <Link to="/register" className="text-primary font-medium">Get started</Link></p>
                    <p className="mt-4 text-center text-xs text-foreground/40">Demo: student@laruta.com / Demo@2026</p>
                </div>
            </div>
        </div>
    );
}
